import { useState, useEffect } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'

// crops from useCropData strips successions — fetch them directly by parent IDs
export const useHarvestSummary = (parentCrops) => {
  const [summary, setSummary] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchSummary() {
      setLoading(true)
      setError(null)

      if (!parentCrops?.length) {
        setSummary([])
        setLoading(false)
        return
      }

      try {
        const parentIds = parentCrops.map(c => c.id)
        const { data: rawSuccessions, error: err } = await supabaseGetter.crops.getByParentIds(parentIds)
        if (err) throw err

        // Normalize yield from the active season's schedule (mirrors useCropData logic)
        const successions = (rawSuccessions || []).map(s => {
          const activeSchedule = s.crop_schedules?.find(sc => sc.season?.state === 'active')
            ?? s.crop_schedules?.[0]
          return { ...s, yield: activeSchedule?.yield ?? null }
        })

        const childrenByParent = {}
        for (const s of successions) {
          if (!childrenByParent[s.parent_crop_id]) childrenByParent[s.parent_crop_id] = []
          childrenByParent[s.parent_crop_id].push(s)
        }

        const result = parentCrops.map(parent => {
          const children = childrenByParent[parent.id] || []
          const all = [parent, ...children]
          const plantCount = all.reduce((sum, c) => sum + (c.count || 0), 0)
          const harvested = all.filter(c => c.yield != null)
          const totalYield = harvested.length
            ? harvested.reduce((sum, c) => sum + (c.yield || 0), 0)
            : null
          return {
            id: parent.id,
            name: parent.name,
            plantCount,
            successionCount: children.length,
            harvestCount: harvested.length,
            totalYield,
          }
        }).sort((a, b) => a.name.localeCompare(b.name))

        setSummary(result)
      } catch (err) {
        console.error('Error fetching harvest summary:', err)
        setError(err.message)
        setSummary([])
      } finally {
        setLoading(false)
      }
    }

    fetchSummary()
  }, [parentCrops])

  return { summary, loading, error }
}
